import MapIcon from "@mui/icons-material/Map";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  FilterButton,
  FormTab,
  List,
  NumberInput,
  required,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useNotify,
  useRefresh,
} from "react-admin";
import React from "react";
import { useCommonLabels } from "../i18n/hooks";
import { apiFetch } from "../api/dataProvider";
import { FormSection } from "../ui/forms/FormSection";
import { DescriptionLangFields, TitleLangFields } from "../ui/forms/langFields";
import { LangTabbedForm } from "../ui/forms/LangTabbedForm";

const CATEGORY_CHOICES = [
  { id: "police", name: "Polis (102)" },
  { id: "hospital", name: "Xəstəxana (103)" },
  { id: "psych", name: "Psixoloji dəstək" },
  { id: "helpline", name: "Qaynar xətt" },
  { id: "shelter", name: "Sığınacaq" },
  { id: "other", name: "Digər (yanğın, həkim)" },
];

type SyncSnapshot = {
  CategoryKey?: string;
  categoryKey?: string;
  Latitude?: number;
  latitude?: number;
  Longitude?: number;
  longitude?: number;
  Title?: string;
  title?: string;
  Description?: string;
  description?: string;
  Phone?: string;
  phone?: string;
  PhoneIsEmergencyLine?: boolean;
  phoneIsEmergencyLine?: boolean;
  Website?: string;
  website?: string;
  Address?: string;
  address?: string;
};

type SyncAddItem = { OsmKey?: string; osmKey?: string; Proposed?: SyncSnapshot; proposed?: SyncSnapshot };
type SyncUpdateItem = {
  Id?: number;
  id?: number;
  OsmKey?: string;
  osmKey?: string;
  Current?: SyncSnapshot;
  current?: SyncSnapshot;
  Proposed?: SyncSnapshot;
  proposed?: SyncSnapshot;
  ChangedFields?: string[];
  changedFields?: string[];
};
type SyncDeleteItem = {
  Id?: number;
  id?: number;
  OsmKey?: string;
  osmKey?: string;
  Current?: SyncSnapshot;
  current?: SyncSnapshot;
};

type SyncPreview = {
  ToAdd?: SyncAddItem[];
  toAdd?: SyncAddItem[];
  ToUpdate?: SyncUpdateItem[];
  toUpdate?: SyncUpdateItem[];
  ToRemove?: SyncDeleteItem[];
  toRemove?: SyncDeleteItem[];
  OsmFetchedCount?: number;
  osmFetchedCount?: number;
  DbActiveOsmCount?: number;
  dbActiveOsmCount?: number;
};

type SyncJobStatus = {
  JobId?: string;
  jobId?: string;
  Status?: string;
  status?: string;
  Error?: string;
  error?: string;
  ProgressCurrent?: number;
  progressCurrent?: number;
  ProgressTotal?: number;
  progressTotal?: number;
  ProgressStep?: string;
  progressStep?: string;
  Result?: SyncPreview;
  result?: SyncPreview;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const apiErrorMessage = (e: unknown, fallback: string) => {
  if (e && typeof e === "object" && "body" in e && e.body && typeof e.body === "object" && "message" in e.body) {
    return String((e.body as { message?: string }).message);
  }
  return e instanceof Error ? e.message : fallback;
};

const filters = [
  <SearchInput key="q" source="q" alwaysOn />,
  <SelectInput key="category" source="CategoryKey" choices={CATEGORY_CHOICES} emptyText="Bütün kateqoriyalar" />,
  <BooleanInput key="active" source="Active" defaultValue={true} />,
];

const snapVal = (snap: SyncSnapshot | undefined, key: keyof SyncSnapshot): string => {
  if (!snap) return "—";
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  const raw = snap[pascal as keyof SyncSnapshot] ?? snap[key];
  if (typeof raw === "boolean") return raw ? "bəli" : "xeyr";
  if (raw == null || raw === "") return "—";
  return String(raw);
};

const formatCoords = (snap?: SyncSnapshot) => {
  const lat = snap?.Latitude ?? snap?.latitude;
  const lng = snap?.Longitude ?? snap?.longitude;
  if (lat == null || lng == null) return "—";
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

const OsmSyncDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = React.useState(false);
  const [progressText, setProgressText] = React.useState("");
  const [applying, setApplying] = React.useState(false);
  const [tab, setTab] = React.useState(0);
  const [preview, setPreview] = React.useState<SyncPreview | null>(null);
  const [selectedAdds, setSelectedAdds] = React.useState<Set<string>>(new Set());
  const [selectedUpdates, setSelectedUpdates] = React.useState<Set<number>>(new Set());
  const [selectedDeletes, setSelectedDeletes] = React.useState<Set<number>>(new Set());

  const toAdd = preview?.ToAdd ?? preview?.toAdd ?? [];
  const toUpdate = preview?.ToUpdate ?? preview?.toUpdate ?? [];
  const toRemove = preview?.ToRemove ?? preview?.toRemove ?? [];

  const loadPreview = async () => {
    setLoading(true);
    setProgressText("OSM sorğusu başladı…");
    setPreview(null);
    try {
      const start = (await apiFetch("/api/phmappoint/syncPreview/start", {
        method: "POST",
        body: JSON.stringify({ Lat: 40.4093, Lng: 49.8671, RadiusM: 250000 }),
      })) as { JobId?: string; jobId?: string };

      const jobId = start.JobId ?? start.jobId;
      if (!jobId) {
        throw new Error("Sync job id alınmadı");
      }

      for (let attempt = 0; attempt < 150; attempt += 1) {
        await sleep(2000);
        const status = (await apiFetch(`/api/phmappoint/syncPreview/job/${jobId}`)) as SyncJobStatus;
        const state = (status.Status ?? status.status ?? "").toLowerCase();
        const current = status.ProgressCurrent ?? status.progressCurrent ?? 0;
        const total = status.ProgressTotal ?? status.progressTotal ?? 6;
        const step = status.ProgressStep ?? status.progressStep ?? "";
        if (current > 0) {
          setProgressText(`OpenStreetMap: ${step} (${current}/${total})…`);
        }

        if (state === "completed") {
          const result = (status.Result ?? status.result) as SyncPreview | undefined;
          if (!result) {
            throw new Error("Sync job completed without result");
          }
          setPreview(result);
          setSelectedAdds(new Set(toAddList(result).map((item) => item.osmKey)));
          setSelectedUpdates(new Set(toUpdateList(result).map((item) => item.id)));
          setSelectedDeletes(new Set(toRemoveList(result).map((item) => item.id)));
          setTab(0);
          notify("OSM diff hazırdır", { type: "info" });
          return;
        }

        if (state === "failed") {
          throw new Error(status.Error ?? status.error ?? "OSM sync failed");
        }
      }

      throw new Error("OSM sync çox uzun çəkdi (7+ dəq). Yenidən cəhd edin.");
    } catch (e) {
      notify(apiErrorMessage(e, "OSM sorğusu uğursuz oldu"), { type: "error" });
    } finally {
      setLoading(false);
      setProgressText("");
    }
  };

  const toAddList = (p: SyncPreview) =>
    (p.ToAdd ?? p.toAdd ?? []).map((item) => ({
      osmKey: item.OsmKey ?? item.osmKey ?? "",
      proposed: item.Proposed ?? item.proposed ?? {},
    }));

  const toUpdateList = (p: SyncPreview) =>
    (p.ToUpdate ?? p.toUpdate ?? []).map((item) => ({
      id: Number(item.Id ?? item.id ?? 0),
      osmKey: item.OsmKey ?? item.osmKey ?? "",
      current: item.Current ?? item.current ?? {},
      proposed: item.Proposed ?? item.proposed ?? {},
      changedFields: item.ChangedFields ?? item.changedFields ?? [],
    }));

  const toRemoveList = (p: SyncPreview) =>
    (p.ToRemove ?? p.toRemove ?? []).map((item) => ({
      id: Number(item.Id ?? item.id ?? 0),
      osmKey: item.OsmKey ?? item.osmKey ?? "",
      current: item.Current ?? item.current ?? {},
    }));

  React.useEffect(() => {
    if (open && !preview && !loading) {
      void loadPreview();
    }
    if (!open) {
      setPreview(null);
      setProgressText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleAdd = (key: string) => {
    setSelectedAdds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleUpdate = (id: number) => {
    setSelectedUpdates((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDelete = (id: number) => {
    setSelectedDeletes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applySelected = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const adds = toAddList(preview)
        .filter((item) => selectedAdds.has(item.osmKey))
        .map((item) => ({ OsmKey: item.osmKey, Proposed: item.proposed }));
      const updates = toUpdateList(preview)
        .filter((item) => selectedUpdates.has(item.id))
        .map((item) => ({
          Id: item.id,
          OsmKey: item.osmKey,
          Current: item.current,
          Proposed: item.proposed,
          ChangedFields: item.changedFields,
        }));
      const deleteIds = toRemoveList(preview)
        .filter((item) => selectedDeletes.has(item.id))
        .map((item) => item.id);

      const result = (await apiFetch("/api/phmappoint/syncApply", {
        method: "POST",
        body: JSON.stringify({ Adds: adds, Updates: updates, DeleteIds: deleteIds }),
      })) as { Added?: number; Updated?: number; Removed?: number };

      notify(
        `Tətbiq edildi: +${result.Added ?? 0} / ~${result.Updated ?? 0} / −${result.Removed ?? 0}`,
        { type: "success" },
      );
      refresh();
      onClose();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Sync tətbiqi uğursuz oldu", { type: "error" });
    } finally {
      setApplying(false);
    }
  };

  const adds = toAddList(preview ?? {});
  const updates = toUpdateList(preview ?? {});
  const removes = toRemoveList(preview ?? {});

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>OSM sinxronizasiyası (diff)</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Alert severity="info">
            {progressText || "OpenStreetMap sorğulanır… (bir neçə dəqiqə)"}
          </Alert>
        )}
        {!loading && preview && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              OSM: {preview.OsmFetchedCount ?? preview.osmFetchedCount ?? 0} nöqtə · DB (OSM):{" "}
              {preview.DbActiveOsmCount ?? preview.dbActiveOsmCount ?? 0}
            </Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label={`Əlavə (${adds.length})`} />
              <Tab label={`Yenilə (${updates.length})`} />
              <Tab label={`Sil (${removes.length})`} />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ maxHeight: 360, overflow: "auto" }}>
                {adds.length === 0 && <Typography variant="body2">Yeni nöqtə yoxdur</Typography>}
                {adds.map((item) => (
                  <Box key={item.osmKey} sx={{ mb: 1.5, p: 1, border: "1px solid #eee", borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAdds.has(item.osmKey)}
                          onChange={() => toggleAdd(item.osmKey)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>{snapVal(item.proposed, "title")}</strong> · {item.osmKey} ·{" "}
                          {snapVal(item.proposed, "categoryKey")} · {formatCoords(item.proposed)}
                        </Typography>
                      }
                    />
                  </Box>
                ))}
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ maxHeight: 360, overflow: "auto" }}>
                {updates.length === 0 && <Typography variant="body2">Dəyişiklik yoxdur</Typography>}
                {updates.map((item) => (
                  <Box key={item.id} sx={{ mb: 1.5, p: 1, border: "1px solid #eee", borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedUpdates.has(item.id)}
                          onChange={() => toggleUpdate(item.id)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>{snapVal(item.current, "title")}</strong> · {item.osmKey}
                        </Typography>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Dəyişən: {item.changedFields.join(", ") || "—"}
                    </Typography>
                    <Typography variant="caption" display="block">
                      → {snapVal(item.proposed, "title")} · tel: {snapVal(item.proposed, "phone")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ maxHeight: 360, overflow: "auto" }}>
                {removes.length === 0 && <Typography variant="body2">Silinəcək nöqtə yoxdur</Typography>}
                {removes.map((item) => (
                  <Box key={item.id} sx={{ mb: 1.5, p: 1, border: "1px solid #eee", borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedDeletes.has(item.id)}
                          onChange={() => toggleDelete(item.id)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          <strong>{snapVal(item.current, "title")}</strong> · {item.osmKey} · DB #{item.id}
                        </Typography>
                      }
                    />
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Bağla</Button>
        <Button onClick={() => void loadPreview()} disabled={loading || applying}>
          Yenidən yüklə
        </Button>
        <Button
          variant="contained"
          onClick={() => void applySelected()}
          disabled={loading || applying || !preview}
        >
          Seçilənləri tətbiq et
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ListActions = () => {
  const [syncOpen, setSyncOpen] = React.useState(false);
  return (
    <TopToolbar>
      <FilterButton />
      <Button
        variant="contained"
        startIcon={<SyncIcon />}
        onClick={() => setSyncOpen(true)}
        size="small"
      >
        OpenStreetMap import
      </Button>
      <CreateButton />
      <OsmSyncDialog open={syncOpen} onClose={() => setSyncOpen(false)} />
    </TopToolbar>
  );
};

const MapPointEmpty = () => {
  const [syncOpen, setSyncOpen] = React.useState(false);
  return (
    <>
      <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 520, mx: "auto" }}>
          Xəritə nöqtələri hələ yoxdur. OpenStreetMap-dən import edin (diff ilə) və ya əl ilə əlavə edin.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
          <Button variant="contained" startIcon={<SyncIcon />} onClick={() => setSyncOpen(true)}>
            OpenStreetMap import
          </Button>
          <CreateButton variant="outlined" />
        </Box>
      </Box>
      <OsmSyncDialog open={syncOpen} onClose={() => setSyncOpen(false)} />
    </>
  );
};

const PhMapPointForm = () => {
  const c = useCommonLabels();
  return (
    <LangTabbedForm>
      <FormTab label="Ümumi">
        <FormSection title="Kateqoriya və koordinatlar">
          <SelectInput
            source="CategoryKey"
            label="Kateqoriya"
            choices={CATEGORY_CHOICES}
            validate={required()}
            fullWidth
          />
          <TextInput source="OsmKey" label="OSM key (node-123)" fullWidth />
          <NumberInput source="Latitude" label="Enlik" validate={required()} fullWidth />
          <NumberInput source="Longitude" label="Uzunluq" validate={required()} fullWidth />
          <TextInput source="Phone" label="Telefon" fullWidth />
          <BooleanInput source="PhoneIsEmergencyLine" label="Təcili xətt (102/103/116111)" />
          <TextInput source="Website" label="Vebsayt" fullWidth />
          <TextInput source="Address" label="Ünvan" fullWidth multiline />
          <BooleanInput source="Active" label={c.active} defaultValue />
          <BooleanInput
            source="AdminLocked"
            label="Admin kilidi (OSM sync toxunmasın)"
            defaultValue={false}
          />
        </FormSection>
      </FormTab>
      <FormTab label="🇦🇿 AZ">
        <TitleLangFields lang="AZ" titleRequired />
        <DescriptionLangFields lang="AZ" rows={3} />
      </FormTab>
      <FormTab label="🇷🇺 RU">
        <TitleLangFields lang="RU" titleRequired />
        <DescriptionLangFields lang="RU" rows={3} />
      </FormTab>
      <FormTab label="🇬🇧 EN">
        <TitleLangFields lang="EN" titleRequired />
        <DescriptionLangFields lang="EN" rows={3} />
      </FormTab>
    </LangTabbedForm>
  );
};

export const PhMapPointList = () => {
  const c = useCommonLabels();
  return (
    <List
      filters={filters}
      sort={{ field: "Id", order: "DESC" }}
      actions={<ListActions />}
      empty={<MapPointEmpty />}
      perPage={25}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="CategoryKey" label="Kateqoriya" />
        <TextField source="TitleAZ" label="Başlıq (AZ)" />
        <TextField source="Phone" label="Telefon" />
        <TextField source="OsmKey" label="OSM" />
        <BooleanField source="AdminLocked" label="Kilid" />
        <BooleanField source="Active" label={c.status} />
      </Datagrid>
    </List>
  );
};

export const PhMapPointEdit = () => (
  <Edit mutationMode="pessimistic">
    <PhMapPointForm />
  </Edit>
);

export const PhMapPointCreate = () => (
  <Create>
    <PhMapPointForm />
  </Create>
);

export const phMapPointIcon = MapIcon;
